/**
 * roiModel.ts — pure EV-charger ROI calculation engine (T-020, Phase 1).
 *
 * A faithful TypeScript port of Noreen's `OptiYield_ROI_Calculators.xlsx` workbook
 * (source_materials/, internal). The workbook's two calculator tabs — "DCFC ROI" and "L2 ROI" —
 * are the SAME model with different ship defaults; this module is that one model. Default input
 * sets and the Read Me benchmark ranges live in `./presets`.
 *
 * SCOPE (Phase 1): pure calculation only — no UI, no route, no ZIP→rate lookup, no PDF. The cell
 * references in the comments (Bnn) point at the "DCFC ROI"/"L2 ROI" tabs so any future reviewer can
 * diff a formula against the sheet. The sheet is the spec: formulas are mirrored EXACTLY, including
 * its divide-by-zero guards and its "n/a" outputs, which are represented here as `null`.
 *
 * Fidelity notes worth a reviewer's eye (ported as-is, NOT silently changed):
 *   - `effectiveCostPerKwh` counts energy + demand charge ONLY (sheet B39 / note C39) — it excludes
 *     network, maintenance, insurance and lease. It is the "profitability killer" gap metric, not a
 *     fully-loaded unit cost.
 *   - `breakEvenPricePerKwh` = totalOpex / annualKwh (sheet B53). It ignores idle-fee and other
 *     revenue and ignores CapEx recovery — a deliberately conservative "covers OpEx only" figure.
 *   - Cash-flow growth uses exponent (year − 1): year 1 sits at the base revenue/OpEx and growth /
 *     escalation first bite in year 2 (sheet C62: `(1+g)^(C61-1)`).
 *   - NPV sums discounted flows for years 0–10; year 0 (the −netCapex outflow) is discounted at
 *     factor 1, i.e. included undiscounted (sheet B67 = SUM(B65:L65)).
 *   - IRR uses a robust bisection solver rather than replicating Excel's Newton iteration; where the
 *     sheet's `IFERROR(IRR(...),"n/a")` would surface "n/a" (no sign change / no root) this returns
 *     `null`.
 */

/** The 24 editable ("yellow") inputs of a single calculator tab. All monetary values are USD. */
export interface RoiInputs {
  // Site & charger configuration
  /** Number of charging ports (sheet B5). */
  ports: number
  /** Rated power per port, kW (B6). */
  ratedPowerKw: number
  /** Operating days per year (B7). */
  operatingDays: number
  // Throughput
  /** Energy dispensed per port per day, kWh — the single most sensitive input (B10). */
  kwhPerPortPerDay: number
  /** Average kWh per charging session, for the sessions/day sanity check (B13). */
  avgKwhPerSession: number
  // Capital cost (one-time)
  /** Charger hardware per port (B17). */
  hardwarePerPort: number
  /** Make-ready + installation per port (B18). */
  makeReadyPerPort: number
  /** Electrical / utility upgrades, site total (B19). */
  electricalUpgradesSite: number
  /** Permitting + soft costs, site total (B20). */
  permittingSoftCostsSite: number
  /** Other / contingency, site total (B21). */
  contingencySite: number
  /** Incentives / rebates applied, site total (B23). 30C expired 6/30/26 — state/utility/LCFS only. */
  incentives: number
  // Operating cost (annual)
  /** Utility energy rate, $/kWh (B27). */
  utilityRatePerKwh: number
  /** Demand charge, $/kW-month (B28). */
  demandChargePerKwMonth: number
  /** Billed peak demand, kW (B29). */
  billedPeakKw: number
  /** Network / software per port per year (B30). */
  networkPerPortYr: number
  /** Maintenance per port per year (B31). */
  maintenancePerPortYr: number
  /** Insurance + other fixed, site per year (B32). */
  insuranceFixedSiteYr: number
  /** Land lease, site per year (B33). */
  landLeaseSiteYr: number
  // Revenue (annual)
  /** Price to driver, $/kWh (B42). */
  priceToDriverPerKwh: number
  /** Idle-fee revenue, site per year (B43). */
  idleFeeRevenueYr: number
  /** Other revenue — ads, retail uplift, site per year (B44). */
  otherRevenueYr: number
  // 10-year cash flow assumptions
  /** Discount rate, e.g. 0.08 (B56). */
  discountRate: number
  /** Annual revenue growth, e.g. 0.03 (B57). */
  revenueGrowth: number
  /** Annual cost escalation, e.g. 0.025 (B58). */
  costEscalation: number
}

/** One year of the 10-year cash-flow table (years 0–10). */
export interface CashFlowYear {
  /** 0 = today (the −netCapex outflow), 1–10 = operating years. */
  year: number
  /** Net cash flow for the year (sheet row 62). */
  netCashFlow: number
  /** Running cumulative net cash flow (sheet row 63). */
  cumulativeCashFlow: number
  /** Discount factor 1/(1+r)^year (sheet row 64). */
  discountFactor: number
  /** netCashFlow × discountFactor (sheet row 65). */
  discountedCashFlow: number
}

/** The full computed result set for one calculator run. `null` mirrors the sheet's "n/a". */
export interface RoiResults {
  /** Annual energy dispensed, kWh (B11). */
  annualKwh: number
  /** Implied load factor, dispensed vs rated — energy basis, not hours occupied (B12). */
  impliedLoadFactor: number
  /** Implied sessions per port per day, sanity check (B14). */
  impliedSessionsPerPortPerDay: number
  /** Gross capital cost (B22). */
  grossCapex: number
  /** Net capital cost after incentives — what you actually finance (B24). */
  netCapex: number
  /** Annual energy cost (B34). */
  annualEnergyCost: number
  /** Annual demand charge (B35). */
  annualDemandCharge: number
  /** Annual network + maintenance (B36). */
  annualNetworkMaintenance: number
  /** Annual insurance + lease (B37). */
  annualInsuranceLease: number
  /** Total annual OpEx (B38). */
  totalOpex: number
  /** Effective cost per kWh delivered — energy + demand only (B39). */
  effectiveCostPerKwh: number
  /** Charging revenue (B45). */
  chargingRevenue: number
  /** Total annual revenue (B46). */
  totalRevenue: number
  /** Annual net operating income (B49). */
  noi: number
  /** Gross margin per kWh — price to driver minus effective cost (B50). */
  grossMarginPerKwh: number
  /** Simple payback in years, or null when NOI ≤ 0 (B51 "n/a"). */
  simplePaybackYears: number | null
  /** Year-1 cash-on-cash return, or null when net capital ≤ 0 (B52 "n/a"). */
  cashOnCashReturn: number | null
  /** Break-even price to driver covering OpEx only (B53). */
  breakEvenPricePerKwh: number
  /** 10-year cash-flow table, years 0–10 (rows 61–65). */
  cashFlow: CashFlowYear[]
  /** Net present value, sum of discounted years 0–10 (B67). */
  npv: number
  /** Internal rate of return over the 10-year horizon, or null when unsolvable (B68 "n/a"). */
  irr: number | null
}

/** Number of operating years modelled after year 0 (sheet columns C–L). */
const HORIZON_YEARS = 10

/**
 * Internal rate of return via bisection over [-0.99, 10] on the cash-flow vector.
 *
 * Returns the rate where NPV = 0, or `null` when the flows have no sign change (all outflows or all
 * inflows) — the case the sheet's `IFERROR(IRR(...),"n/a")` renders as "n/a". Bisection is used in
 * place of Excel's Newton iteration deliberately: it cannot diverge and needs no seed guess, so the
 * engine's "solvable / n/a" boundary is deterministic. `flows[0]` is the year-0 cash flow.
 */
export function internalRateOfReturn(flows: number[], lower = -0.99, upper = 10): number | null {
  const npv = (rate: number): number => flows.reduce((acc, cf, i) => acc + cf / (1 + rate) ** i, 0)

  let lo = lower
  let hi = upper
  let fLo = npv(lo)
  let fHi = npv(hi)
  if (fLo === 0) return lo
  if (fHi === 0) return hi
  // No bracketed root: NPV doesn't cross zero across the search interval.
  if (fLo > 0 === fHi > 0) return null

  for (let i = 0; i < 200; i++) {
    const mid = (lo + hi) / 2
    const fMid = npv(mid)
    if (Math.abs(fMid) < 1e-10) return mid
    if (fMid > 0 === fLo > 0) {
      lo = mid
      fLo = fMid
    } else {
      hi = mid
      fHi = fMid
    }
  }
  return (lo + hi) / 2
}

/**
 * Compute the full ROI result set from a single calculator's inputs. Pure: no I/O, no mutation of
 * `inputs`. Every line maps to a sheet cell (referenced in the trailing comment) and is mirrored
 * exactly, including the sheet's divide-by-zero guards and its "n/a" (→ `null`) outputs.
 */
export function computeRoi(inputs: RoiInputs): RoiResults {
  const {
    ports,
    ratedPowerKw,
    operatingDays,
    kwhPerPortPerDay,
    avgKwhPerSession,
    hardwarePerPort,
    makeReadyPerPort,
    electricalUpgradesSite,
    permittingSoftCostsSite,
    contingencySite,
    incentives,
    utilityRatePerKwh,
    demandChargePerKwMonth,
    billedPeakKw,
    networkPerPortYr,
    maintenancePerPortYr,
    insuranceFixedSiteYr,
    landLeaseSiteYr,
    priceToDriverPerKwh,
    idleFeeRevenueYr,
    otherRevenueYr,
    discountRate,
    revenueGrowth,
    costEscalation,
  } = inputs

  // Throughput
  const annualKwh = ports * kwhPerPortPerDay * operatingDays // B11
  const impliedLoadFactor = ratedPowerKw > 0 ? kwhPerPortPerDay / (ratedPowerKw * 24) : 0 // B12
  const impliedSessionsPerPortPerDay =
    avgKwhPerSession > 0 ? kwhPerPortPerDay / avgKwhPerSession : 0 // B14

  // Capital cost
  const grossCapex =
    ports * (hardwarePerPort + makeReadyPerPort) +
    electricalUpgradesSite +
    permittingSoftCostsSite +
    contingencySite // B22
  const netCapex = grossCapex - incentives // B24

  // Operating cost
  const annualEnergyCost = annualKwh * utilityRatePerKwh // B34
  const annualDemandCharge = demandChargePerKwMonth * billedPeakKw * 12 // B35
  const annualNetworkMaintenance = ports * (networkPerPortYr + maintenancePerPortYr) // B36
  const annualInsuranceLease = insuranceFixedSiteYr + landLeaseSiteYr // B37
  const totalOpex =
    annualEnergyCost + annualDemandCharge + annualNetworkMaintenance + annualInsuranceLease // B38
  const effectiveCostPerKwh =
    annualKwh > 0 ? (annualEnergyCost + annualDemandCharge) / annualKwh : 0 // B39 (energy+demand only)

  // Revenue
  const chargingRevenue = priceToDriverPerKwh * annualKwh // B45
  const totalRevenue = chargingRevenue + idleFeeRevenueYr + otherRevenueYr // B46

  // Returns summary
  const noi = totalRevenue - totalOpex // B49
  const grossMarginPerKwh = priceToDriverPerKwh - effectiveCostPerKwh // B50
  const simplePaybackYears = noi > 0 ? netCapex / noi : null // B51 ("n/a" when NOI ≤ 0)
  const cashOnCashReturn = netCapex > 0 ? noi / netCapex : null // B52 ("n/a" when netCapex ≤ 0)
  const breakEvenPricePerKwh = annualKwh > 0 ? totalOpex / annualKwh : 0 // B53

  // 10-year cash flow (rows 61–65)
  const cashFlow: CashFlowYear[] = []
  let cumulative = 0
  for (let year = 0; year <= HORIZON_YEARS; year++) {
    const netCashFlow =
      year === 0
        ? -netCapex // B62 (year 0) = -netCapex
        : totalRevenue * (1 + revenueGrowth) ** (year - 1) -
          totalOpex * (1 + costEscalation) ** (year - 1) // C62..L62
    cumulative += netCashFlow // row 63 (running sum)
    const discountFactor = 1 / (1 + discountRate) ** year // row 64
    cashFlow.push({
      year,
      netCashFlow,
      cumulativeCashFlow: cumulative,
      discountFactor,
      discountedCashFlow: netCashFlow * discountFactor, // row 65
    })
  }

  const npv = cashFlow.reduce((acc, y) => acc + y.discountedCashFlow, 0) // B67 = SUM(B65:L65)
  const irr = internalRateOfReturn(cashFlow.map((y) => y.netCashFlow)) // B68 = IFERROR(IRR,"n/a")

  return {
    annualKwh,
    impliedLoadFactor,
    impliedSessionsPerPortPerDay,
    grossCapex,
    netCapex,
    annualEnergyCost,
    annualDemandCharge,
    annualNetworkMaintenance,
    annualInsuranceLease,
    totalOpex,
    effectiveCostPerKwh,
    chargingRevenue,
    totalRevenue,
    noi,
    grossMarginPerKwh,
    simplePaybackYears,
    cashOnCashReturn,
    breakEvenPricePerKwh,
    cashFlow,
    npv,
    irr,
  }
}
